import React, { useState, useEffect } from 'react';
import { Button, Card, Container, Row, Col, Modal } from 'react-bootstrap';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import styled from 'styled-components';

// ✅ Styled Components
const StyledContainer = styled(Container)`
  background-color: #232830;
  color: #FFFFFF;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const StyledHeading = styled.h2`
  font-size: 22px;
  font-weight: bold;
  color: #FF7C24;
  text-align: center;
  margin-bottom: 20px;
`;

const QuestionText = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: #FFFFFF;
  text-align: center;
  margin-bottom: 20px;
`;

// ✅ Standardized "Continue" Button
const StandardButton = styled(Button)`
  background-color: ${(props) => (props.disabled ? '#555' : '#FF7C24')};
  border-color: ${(props) => (props.disabled ? '#555' : '#FF7C24')};
  color: #FFFFFF;
  font-size: 18px;
  font-weight: bold;
  border-radius: 12px;
  padding: 12px;
  width: 80%;
  max-width: 300px;
  margin-top: 20px;
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${(props) => (props.disabled ? '#555' : '#E66A1A')};
    border-color: ${(props) => (props.disabled ? '#555' : '#E66A1A')};
  }
`;

const MatchTheFollowingPSQuestion = ({ question, onNext }) => {
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState({});
  const [incorrectPairs, setIncorrectPairs] = useState([]);
  const [firstSelection, setFirstSelection] = useState(null);

  const db = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    setMatchedPairs({});
    setIncorrectPairs([]);
    setSelectedLeft(null);
    setSelectedRight(null);
    setFirstSelection(null);
  }, [question]);

  const correctAnswers = Object.fromEntries(
    question.correctanswer.split(',').map(pair => pair.split(':'))
  );

  const handleSelection = (option, isLeft) => {
    if (isLeft) {
      setSelectedLeft(option);
      setFirstSelection(option);
    } else {
      setSelectedRight(option);
    }

    if (selectedLeft && !isLeft) {
      if (correctAnswers[selectedLeft] === option) {
        setMatchedPairs(prev => ({ ...prev, [selectedLeft]: option }));
      } else {
        setIncorrectPairs([selectedLeft, option]);

        setTimeout(() => setIncorrectPairs([]), 1000);
      }

      setTimeout(() => setFirstSelection(null), 500);
      setSelectedLeft(null);
      setSelectedRight(null);
    }
  };

  const getButtonStyle = (option) => {
    if (matchedPairs[option] || Object.values(matchedPairs).includes(option)) {
      return { backgroundColor: 'green', color: 'white' };
    }
    if (incorrectPairs.includes(option)) {
      return { backgroundColor: 'red', color: 'white' };
    }
    if (firstSelection === option) {
      return { backgroundColor: 'purple', color: 'white' };
    }
    return { backgroundColor: 'white', color: 'black' };
  };

  const handleContinue = async () => {
    try {
      const currentUser = auth.currentUser;
      await addDoc(collection(db, 'userpracticesessionanswers'), {
        date: serverTimestamp(),
        lessonid: question.lessonid,
        questionid: question.questionid,
        submittedanswer: Object.entries(matchedPairs).map(([left, right]) => `${left}:${right}`).join(','),
        userid: currentUser ? currentUser.uid : 'anonymous',
        iscorrect: true,
      });
      onNext(true);
    } catch (error) {
      console.error('Error logging answer:', error);
    }
  };

  return (
    <StyledContainer>
      <StyledHeading>Match the Following</StyledHeading>
      <QuestionText>{question.questionname}</QuestionText>

      {/* ✅ Matching Options Layout with spacing */}
      <Row className="g-3" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <Col xs={5}>
          {[1, 2, 3, 4, 5].map(i => (
            question[`answeroption${i}`] && (
              <Button
                key={i}
                onClick={() => handleSelection(`answeroption${i}`, true)}
                disabled={matchedPairs[`answeroption${i}`]}
                style={{
                  ...getButtonStyle(`answeroption${i}`),
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  marginBottom: '15px', // ✅ Added spacing between options
                }}
              >
                {question[`answeroption${i}`]}
              </Button>
            )
          ))}
        </Col>

        <Col xs={5}>
          {[6, 7, 8, 9, 10].map(i => (
            question[`answeroption${i}`] && (
              <Button
                key={i}
                onClick={() => handleSelection(`answeroption${i}`, false)}
                disabled={matchedPairs[`answeroption${i}`]}
                style={{
                  ...getButtonStyle(`answeroption${i}`),
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  marginBottom: '15px', // ✅ Added spacing between options
                }}
              >
                {question[`answeroption${i}`]}
              </Button>
            )
          ))}
        </Col>
      </Row>

      {/* ✅ Centered Continue Button when all matched */}
      {Object.keys(matchedPairs).length === Object.keys(correctAnswers).length && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <StandardButton onClick={handleContinue}>Continue</StandardButton>
        </div>
      )}

      {/* Paywall Modal */}
      <Modal show={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>Unlock More Practice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Upgrade to FinTorro Pro to unlock unlimited lessons and practice sessions.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger">Upgrade Now</Button>
        </Modal.Footer>
      </Modal>
    </StyledContainer>
  );
};

export default MatchTheFollowingPSQuestion;



// import React, { useState, useEffect } from 'react';
// import { Button, Card, Container, Row, Col, Modal } from 'react-bootstrap';
// import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
// import { getAuth } from 'firebase/auth';
// import styled from 'styled-components';

// // ✅ Styled Components
// const StyledContainer = styled(Container)`
//   background-color: #232830;
//   color: #FFFFFF;
//   min-height: 100vh;
//   display: flex;
//   flex-direction: column;
//   justify-content: center;
//   align-items: center;
//   padding: 20px;
// `;

// const StyledHeading = styled.h2`
//   font-size: 22px;
//   font-weight: bold;
//   color: #FF7C24;
//   text-align: center;
//   margin-bottom: 20px;
// `;

// const QuestionText = styled.h3`
//   font-size: 20px;
//   font-weight: bold;
//   color: #FFFFFF;
//   text-align: center;
//   margin-bottom: 20px;
// `;

// // ✅ Standardized "Continue" Button
// const StandardButton = styled(Button)`
//   background-color: ${(props) => (props.disabled ? '#555' : '#FF7C24')};
//   border-color: ${(props) => (props.disabled ? '#555' : '#FF7C24')};
//   color: #FFFFFF;
//   font-size: 18px;
//   font-weight: bold;
//   border-radius: 12px;
//   padding: 12px;
//   width: 80%;
//   max-width: 300px;
//   margin-top: 20px;
//   transition: background-color 0.2s ease-in-out;
//   &:hover {
//     background-color: ${(props) => (props.disabled ? '#555' : '#E66A1A')};
//     border-color: ${(props) => (props.disabled ? '#555' : '#E66A1A')};
//   }
// `;

// const MatchTheFollowingPSQuestion = ({ question, onNext, maxWrongAnswersRemaining, onUpgrade }) => {
//   const [selectedLeft, setSelectedLeft] = useState(null);
//   const [selectedRight, setSelectedRight] = useState(null);
//   const [matchedPairs, setMatchedPairs] = useState({});
//   const [incorrectPairs, setIncorrectPairs] = useState([]);
//   const [firstSelection, setFirstSelection] = useState(null);
//   const [wrongAttempts, setWrongAttempts] = useState(0);
//   const [showPaywall, setShowPaywall] = useState(false);

//   const db = getFirestore();
//   const auth = getAuth();

//   // ✅ Parse correct answer pairs from question data
//   const correctAnswers = Object.fromEntries(
//     question.correctanswer.split(',').map(pair => pair.split(':'))
//   );

//   // ✅ Reset state when a new question is loaded
//   useEffect(() => {
//     setMatchedPairs({});
//     setIncorrectPairs([]);
//     setSelectedLeft(null);
//     setSelectedRight(null);
//     setFirstSelection(null);
//     setWrongAttempts(0);
//   }, [question]);

//   const handleSelection = (option, isLeft) => {
//     if (isLeft) {
//       setSelectedLeft(option);
//       setFirstSelection(option);
//     } else {
//       setSelectedRight(option);
//     }

//     if (selectedLeft && !isLeft) {
//       if (correctAnswers[selectedLeft] === option) {
//         setMatchedPairs(prev => ({ ...prev, [selectedLeft]: option }));
//       } else {
//         setIncorrectPairs([selectedLeft, option]);
//         setWrongAttempts(prev => prev + 1);

//         if (wrongAttempts + 1 > maxWrongAnswersRemaining) {
//           setShowPaywall(true);
//         }

//         setTimeout(() => setIncorrectPairs([]), 1000);
//       }

//       setTimeout(() => setFirstSelection(null), 500);
//       setSelectedLeft(null);
//       setSelectedRight(null);
//     }
//   };

//   const getButtonStyle = (option) => {
//     if (matchedPairs[option] || Object.values(matchedPairs).includes(option)) {
//       return { backgroundColor: 'green', color: 'white' };
//     }
//     if (incorrectPairs.includes(option)) {
//       return { backgroundColor: 'red', color: 'white' };
//     }
//     if (firstSelection === option) {
//       return { backgroundColor: 'purple', color: 'white' };
//     }
//     return { backgroundColor: 'white', color: 'black' };
//   };

//   const handleContinue = async () => {
//     try {
//       const currentUser = auth.currentUser;
//       await addDoc(collection(db, 'userpracticesessionanswers'), {
//         date: serverTimestamp(),
//         lessonid: question.lessonid,
//         questionid: question.questionid,
//         submittedanswer: Object.entries(matchedPairs).map(([left, right]) => `${left}:${right}`).join(','),
//         userid: currentUser ? currentUser.uid : 'anonymous',
//         iscorrect: true,
//       });
//       onNext(true);
//     } catch (error) {
//       console.error('Error logging answer:', error);
//     }
//   };

//   return (
//     <StyledContainer>
//       <StyledHeading>Match the Following</StyledHeading>
//       <QuestionText>{question.questionname}</QuestionText>

//       {/* ✅ Matching Options Layout */}
//       <Row className="g-3" style={{ justifyContent: 'center', alignItems: 'center' }}>
//         <Col xs={5}>
//           {[1, 2, 3, 4, 5].map(i => (
//             question[`answeroption${i}`] && (
//               <Button
//                 key={i}
//                 onClick={() => handleSelection(`answeroption${i}`, true)}
//                 disabled={matchedPairs[`answeroption${i}`]}
//                 style={{ ...getButtonStyle(`answeroption${i}`), width: '100%', padding: '10px', fontSize: '16px' }}
//               >
//                 {question[`answeroption${i}`]}
//               </Button>
//             )
//           ))}
//         </Col>

//         <Col xs={5}>
//           {[6, 7, 8, 9, 10].map(i => (
//             question[`answeroption${i}`] && (
//               <Button
//                 key={i}
//                 onClick={() => handleSelection(`answeroption${i}`, false)}
//                 disabled={matchedPairs[`answeroption${i}`]}
//                 style={{ ...getButtonStyle(`answeroption${i}`), width: '100%', padding: '10px', fontSize: '16px' }}
//               >
//                 {question[`answeroption${i}`]}
//               </Button>
//             )
//           ))}
//         </Col>
//       </Row>

//       {/* ✅ Centered Continue Button when all matched */}
//       {Object.keys(matchedPairs).length === Object.keys(correctAnswers).length && (
//         <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
//           <StandardButton onClick={handleContinue}>Continue</StandardButton>
//         </div>
//       )}

//       {/* Paywall Modal */}
//       <Modal show={showPaywall} onHide={() => setShowPaywall(false)} centered>
//         <Modal.Header closeButton>
//           <Modal.Title>Unlock More Practice</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <p>Upgrade to FinTorro Pro to unlock unlimited lessons and practice sessions.</p>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="danger" onClick={onUpgrade}>Upgrade Now</Button>
//         </Modal.Footer>
//       </Modal>
//     </StyledContainer>
//   );
// };

// export default MatchTheFollowingPSQuestion;
